import { ApolloLink, Observable } from "@apollo/client";

/**
 * Singleton mutation tracker to monitor ongoing mutations globally, by operation name.
 *
 * Usage:
 *   - `mutationTracker.isMutating` returns true if any mutation is in flight.
 *   - `mutationTracker.getCount(operationName)` returns the count for a specific mutation.
 *   - `mutationTracker.getAllCounts()` returns a map of all operation names to their counts.
 *   - You can subscribe to changes if needed.
 */
class MutationTracker {
  #counts = new Map<string, number>();
  #listeners = new Set<() => void>();

  get isMutating(): boolean {
    return this.#counts.size > 0;
  }

  getCount(operationName: string): number {
    return this.#counts.get(operationName) ?? 0;
  }

  getAllCounts(): Map<string, number> {
    return new Map(this.#counts);
  }

  subscribe(callback: () => void): () => void {
    this.#listeners.add(callback);
    return () => this.#listeners.delete(callback);
  }

  increment(operationName: string): void {
    this.#counts.set(operationName, (this.#counts.get(operationName) ?? 0) + 1);
    this.#notify();
  }

  decrement(operationName: string): void {
    const prev = this.#counts.get(operationName) ?? 0;
    if (prev > 1) {
      this.#counts.set(operationName, prev - 1);
    } else {
      this.#counts.delete(operationName);
    }
    this.#notify();
  }

  #notify(): void {
    this.#listeners.forEach((cb) => {
      cb();
    });
  }
}

const mutationTrackerInstance = new MutationTracker();

export const mutationTracker = {
  get isMutating() {
    return mutationTrackerInstance.isMutating;
  },
  getCount(name: string) {
    return mutationTrackerInstance.getCount(name);
  },
  getAllCounts() {
    return mutationTrackerInstance.getAllCounts();
  },
};

/**
 * Apollo Link to track ongoing mutations by operation name. Increments the mutation counter for the
 * operation on mutation start, decrements on completion/error. This is useful when we want to wait
 * for mutations to finish before proceeding with the next operation in complex compositions that
 * would otherwise be difficult to coordinate and would cause race conditions.
 */
export const mutationTrackingLink = new ApolloLink((operation, forward) => {
  const operationDefinition = operation.query.definitions.find(
    (def) => def.kind === "OperationDefinition" && def.operation === "mutation",
  );
  if (operationDefinition != null) {
    const operationName = operation.operationName ?? ("unknown" as const);
    mutationTrackerInstance.increment(operationName);
    return new Observable((observer) => {
      const subscription = forward(operation).subscribe({
        next: (result) => {
          observer.next?.(result);
        },
        error: (err) => {
          observer.error?.(err);
          mutationTrackerInstance.decrement(operationName);
        },
        complete: () => {
          observer.complete?.();
          mutationTrackerInstance.decrement(operationName);
        },
      });
      return () => {
        subscription.unsubscribe();
      };
    });
  }
  return forward(operation);
});

// Test-only export for unit testing
// @internal-test
export const __test__ = {
  MutationTracker,
  mutationTrackerInstance,
};
